import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiRefreshCw, FiSearch } from "react-icons/fi";
import { MdDevices, MdOpenInNew } from "react-icons/md";
import loginSessionsService from "../../api/services/loginSessionsService";
import SectionCard from "../../components/common/SectionCard";
import PaginationBar from "../../components/common/PaginationBar";
import UserLoginDevicesModal from "./UserLoginDevicesModal";

const ALLOWED = ["Admin", "Developer"];
const PAGE_SIZE = 25;

const PLATFORM_STYLES = {
  web: "bg-sky-100 text-sky-800",
  ios: "bg-gray-900 text-white",
  android: "bg-emerald-100 text-emerald-800",
  unknown: "bg-gray-100 text-gray-600",
};

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function platformLabel(platform) {
  const p = String(platform || "unknown").toLowerCase();
  if (p === "ios") return "iOS";
  if (p === "android") return "Android";
  if (p === "web") return "Web";
  return p || "Unknown";
}

function PlatformBadge({ platform }) {
  const key = String(platform || "unknown").toLowerCase();
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${
        PLATFORM_STYLES[key] || PLATFORM_STYLES.unknown
      }`}
    >
      {platformLabel(key)}
    </span>
  );
}

export default function LoginSessionsPage() {
  const userRole = localStorage.getItem("adminUserRole") || "";
  const [tab, setTab] = useState("active"); // active | history
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [platform, setPlatform] = useState("all");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalUser, setModalUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: PAGE_SIZE,
        ...(search ? { search } : {}),
        ...(platform !== "all" ? { platform } : {}),
      };
      const res =
        tab === "active"
          ? await loginSessionsService.listActive(params)
          : await loginSessionsService.listHistory(params);
      setUsers(res?.users || []);
      setTotal(Number(res?.total) || 0);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load users"
      );
      setUsers([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [tab, page, search, platform]);

  useEffect(() => {
    load();
  }, [load]);

  if (!ALLOWED.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const openUserModal = (row) => {
    setModalUser({
      userId: row.userId,
      id: row.userId,
      name: row.user?.name,
      firstName: row.user?.firstName,
      lastName: row.user?.lastName,
      email: row.user?.email,
    });
  };

  return (
    <div className="w-full min-h-[calc(100vh-120px)] overflow-x-hidden p-4 md:p-6">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MdDevices className="text-[#FB565F]" />
            Login Devices
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            One row per user. Open Devices to see active sessions and previous
            login history.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load()}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          <FiRefreshCw className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => {
            setTab("active");
            setPage(1);
          }}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${
            tab === "active"
              ? "bg-[#FB565F] text-white"
              : "bg-white border border-gray-200 text-gray-700"
          }`}
        >
          Users with active devices
        </button>
        <button
          type="button"
          onClick={() => {
            setTab("history");
            setPage(1);
          }}
          className={`rounded-lg px-4 py-2 text-sm font-semibold ${
            tab === "history"
              ? "bg-[#FB565F] text-white"
              : "bg-white border border-gray-200 text-gray-700"
          }`}
        >
          Users with previous logins
        </button>
      </div>

      <div className="mb-4">
        <SectionCard title="Filters">
          <form
            onSubmit={handleSearch}
            className="flex flex-col gap-3 lg:flex-row lg:items-end"
          >
            <label className="flex-1 block">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Search user / device
              </span>
              <div className="mt-1 relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-red-200"
                  placeholder="Email, name, user id, device model…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
            </label>
            <label className="block w-full lg:w-40">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Platform
              </span>
              <select
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm"
                value={platform}
                onChange={(e) => {
                  setPlatform(e.target.value);
                  setPage(1);
                }}
              >
                <option value="all">All</option>
                <option value="web">Web</option>
                <option value="ios">iOS</option>
                <option value="android">Android</option>
              </select>
            </label>
            <button
              type="submit"
              className="rounded-xl bg-[#FB565F] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#e94a53]"
            >
              Apply
            </button>
          </form>
        </SectionCard>
      </div>

      <SectionCard
        title={
          tab === "active"
            ? "Users currently logged in"
            : "Users with previous login records"
        }
      >
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="px-3 py-3 font-semibold">User</th>
                <th className="px-3 py-3 font-semibold">Active devices</th>
                <th className="px-3 py-3 font-semibold">Previous logins</th>
                <th className="px-3 py-3 font-semibold">Platforms</th>
                <th className="px-3 py-3 font-semibold">Last active</th>
                <th className="px-3 py-3 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-10 text-center text-gray-500"
                  >
                    Loading users…
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-10 text-center text-gray-500"
                  >
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((row) => (
                  <tr
                    key={row.userId}
                    className="border-b border-gray-50 hover:bg-gray-50/80 cursor-pointer"
                    onClick={() => openUserModal(row)}
                  >
                    <td className="px-3 py-3 align-top">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openUserModal(row);
                        }}
                        className="text-left font-semibold text-gray-900 hover:text-[#FB565F]"
                      >
                        {row.user?.name || "—"}
                      </button>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {row.user?.email || "—"}
                      </p>
                      <p className="text-[11px] text-gray-400">
                        #{row.userId}
                      </p>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <span className="inline-flex min-w-[2rem] items-center justify-center rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
                        {row.activeCount}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <span className="inline-flex min-w-[2rem] items-center justify-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-700">
                        {row.previousCount}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="flex flex-wrap gap-1">
                        {(row.platforms || []).length ? (
                          row.platforms.map((p) => (
                            <PlatformBadge key={p} platform={p} />
                          ))
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top text-gray-600 whitespace-nowrap">
                      {formatDate(row.lastActiveAt || row.lastLoginAt)}
                    </td>
                    <td
                      className="px-3 py-3 align-top text-right whitespace-nowrap"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={() => openUserModal(row)}
                        className="inline-flex items-center gap-1 rounded-lg border border-[#FB565F]/30 bg-[#FB565F]/5 px-2.5 py-1.5 text-xs font-semibold text-[#FB565F] hover:bg-[#FB565F]/10"
                      >
                        <MdOpenInNew />
                        Devices
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar
          page={page}
          totalPages={totalPages}
          totalItems={total}
          pageSize={PAGE_SIZE}
          label="users"
          onPageChange={setPage}
        />
      </SectionCard>

      <UserLoginDevicesModal
        open={Boolean(modalUser)}
        user={modalUser}
        onClose={() => setModalUser(null)}
        onChanged={load}
      />
    </div>
  );
}
